export const ANALYSIS_SETTINGS_VERSION = "CER-S v4.6.0";

export type AnalysisSettingsSectionKey =
  | "modules"
  | "beatDetection"
  | "rhythmAnalysis"
  | "qtAnalysisOptions";

export type AnalysisSettingValue = string | boolean;

export interface AnalysisSettings {
  version: string;
  modules: {
    beatToBeatModule: boolean;
    arrhythmiaAnalysisModule: boolean;
    analysisModule: boolean;
    longAnalysisModule: boolean;
  };
  beatDetection: Record<string, string>;
  rhythmAnalysis: Record<string, string>;
  qtAnalysisOptions: Record<string, string>;
}

export interface AnalysisSettingField {
  name: string;
  label: string;
  defaultValue: AnalysisSettingValue;
  type: "boolean" | "number" | "text";
  description: string;
}

export interface AnalysisSettingsSection {
  key: Exclude<AnalysisSettingsSectionKey, "modules">;
  title: string;
  source: string;
  description: string;
  fields: AnalysisSettingField[];
}

export const ANALYSIS_MODULE_FIELDS: AnalysisSettingField[] = [
  {
    name: "beatToBeatModule",
    label: "Beat to beat module",
    defaultValue: true,
    type: "boolean",
    description:
      "Included in the CER-SCor analysis-type JSON. Controls whether beat to beat and QT batch analysis runs during automated processing. Disable it when AMPS support recommends processing the record without QT analysis.",
  },
  {
    name: "arrhythmiaAnalysisModule",
    label: "Arrhythmia analysis module",
    defaultValue: true,
    type: "boolean",
    description:
      "Included in the CER-SCor analysis-type JSON. Controls whether arrhythmia/rhythm analysis is requested for the study.",
  },
  {
    name: "analysisModule",
    label: "Analysis module",
    defaultValue: true,
    type: "boolean",
    description:
      "Included in the CER-SCor analysis-type JSON. Controls whether the standard beat detection analysis module is requested.",
  },
  {
    name: "longAnalysisModule",
    label: "Long analysis module",
    defaultValue: false,
    type: "boolean",
    description:
      "Included in the CER-SCor analysis-type JSON. Controls whether the long analysis module is requested. Leave disabled unless AMPS support asks for it.",
  },
];

export const ANALYSIS_SETTINGS_FILE_SECTIONS: AnalysisSettingsSection[] = [
  {
    key: "beatDetection",
    title: "Beat detection",
    source: "AnalysisModuleOptions.ini",
    description: "Parameters used to detect beats and prepare beat templates before rhythm analysis.",
    fields: [
      {
        name: "AutoDetTimeoutPediatric",
        label: "AutoDetTimeoutPediatric",
        defaultValue: "0",
        type: "number",
        description: "Pediatric automatic detection timeout option. Leave at the default unless AMPS provides a study-specific value.",
      },
      {
        name: "AutoMerge",
        label: "AutoMerge",
        defaultValue: "1",
        type: "number",
        description: "Enables automatic merging behavior for detected beat information.",
      },
      {
        name: "ChnlMask",
        label: "ChnlMask",
        defaultValue: "3",
        type: "number",
        description: "Channel mask used for beat detection input channels.",
      },
      {
        name: "ChnlMaskTemplates",
        label: "ChnlMaskTemplates",
        defaultValue: "3",
        type: "number",
        description: "Channel mask used when creating and matching beat templates.",
      },
      {
        name: "DerMask",
        label: "DerMask",
        defaultValue: "0",
        type: "number",
        description: "Derivative mask option used by the beat detection module.",
      },
      {
        name: "DetThreshold",
        label: "DetThreshold",
        defaultValue: "0",
        type: "number",
        description: "Detection threshold override. The default lets the analysis module use its standard threshold behavior.",
      },
      {
        name: "DetTimeout",
        label: "DetTimeout",
        defaultValue: "290",
        type: "number",
        description: "Detection timeout value used by the beat detection module.",
      },
      {
        name: "EndMinute",
        label: "EndMinute",
        defaultValue: "279360",
        type: "number",
        description: "Minute offset where beat detection should stop. The default covers the full supported processing window.",
      },
      {
        name: "FilterData",
        label: "FilterData",
        defaultValue: "0",
        type: "number",
        description: "Controls whether additional beat detection filtering is applied.",
      },
      {
        name: "FiltersConfig",
        label: "FiltersConfig",
        defaultValue: "0000 40 0.01 60 50 2",
        type: "text",
        description: "Filter configuration string passed to the beat detection module.",
      },
      {
        name: "MinimumNoiseDistance",
        label: "MinimumNoiseDistance",
        defaultValue: "5000",
        type: "number",
        description: "Minimum distance used by noise detection logic.",
      },
      {
        name: "NoiseDetectorSensitivity",
        label: "NoiseDetectorSensitivity",
        defaultValue: "1",
        type: "number",
        description: "Sensitivity level for noise detector behavior.",
      },
      {
        name: "NoiseDetectors",
        label: "NoiseDetectors",
        defaultValue: "511",
        type: "number",
        description: "Noise detector mask defining which detector set is active.",
      },
      {
        name: "StartMinute",
        label: "StartMinute",
        defaultValue: "0",
        type: "number",
        description: "Minute offset where beat detection should start.",
      },
    ],
  },
  {
    key: "rhythmAnalysis",
    title: "Rhythm analysis",
    source: "ArrhythmiaAnalysisModuleOptions.ini",
    description: "Thresholds and options used to identify rhythm events in the processed record.",
    fields: [
      {
        name: "BradyDuration",
        label: "BradyDuration",
        defaultValue: "10000",
        type: "number",
        description: "Minimum bradycardia duration threshold used during rhythm analysis.",
      },
      {
        name: "BradyThreshold",
        label: "BradyThreshold",
        defaultValue: "45",
        type: "number",
        description: "Heart-rate threshold for bradycardia detection.",
      },
      {
        name: "DetectSBeats",
        label: "DetectSBeats",
        defaultValue: "1",
        type: "number",
        description: "Controls detection of supraventricular beats.",
      },
      {
        name: "FindAfibs",
        label: "FindAfibs",
        defaultValue: "1",
        type: "number",
        description: "Controls atrial fibrillation detection during rhythm analysis.",
      },
      {
        name: "FindRhythms",
        label: "FindRhythms",
        defaultValue: "1",
        type: "number",
        description: "Controls rhythm event search during analysis.",
      },
      {
        name: "PauseThreshold",
        label: "PauseThreshold",
        defaultValue: "2500",
        type: "number",
        description: "Pause threshold used to classify long RR pauses.",
      },
      {
        name: "PrematureIndex",
        label: "PrematureIndex",
        defaultValue: "75",
        type: "number",
        description: "Prematurity index used when classifying premature beats.",
      },
      {
        name: "ProlongedRRInterval",
        label: "ProlongedRRInterval",
        defaultValue: "200",
        type: "number",
        description: "RR interval extension threshold used for rhythm classification.",
      },
      {
        name: "RRpWindowSize",
        label: "RRpWindowSize",
        defaultValue: "8",
        type: "number",
        description: "Window size used when evaluating RR patterns.",
      },
      {
        name: "SinusTachyThreshold",
        label: "SinusTachyThreshold",
        defaultValue: "130",
        type: "number",
        description: "Heart-rate threshold for sinus tachycardia detection.",
      },
      {
        name: "SupraVentricularTachyThreshold",
        label: "SupraVentricularTachyThreshold",
        defaultValue: "150",
        type: "number",
        description: "Heart-rate threshold for supraventricular tachycardia detection.",
      },
      {
        name: "TachyDuration",
        label: "TachyDuration",
        defaultValue: "5000",
        type: "number",
        description: "Minimum tachycardia duration threshold used during rhythm analysis.",
      },
      {
        name: "TachyDurationBeats",
        label: "TachyDurationBeats",
        defaultValue: "5",
        type: "number",
        description: "Minimum beat count used with tachycardia duration checks.",
      },
      {
        name: "TachyMinDurationType",
        label: "TachyMinDurationType",
        defaultValue: "0",
        type: "number",
        description: "Selects how minimum tachycardia duration is interpreted.",
      },
      {
        name: "VentricularTachyThreshold",
        label: "VentricularTachyThreshold",
        defaultValue: "150",
        type: "number",
        description: "Heart-rate threshold for ventricular tachycardia detection.",
      },
    ],
  },
  {
    key: "qtAnalysisOptions",
    title: "Beat to beat / QT analysis",
    source: "BeatToBeatModuleOptions.ini",
    description: "Settings used by beat to beat and QT/ST batch analysis.",
    fields: [
      {
        name: "AnalysisMeasures",
        label: "AnalysisMeasures",
        defaultValue: "ALL",
        type: "text",
        description: "Selects which beat to beat analysis measures are calculated.",
      },
      {
        name: "AnalyzeFamilies",
        label: "AnalyzeFamilies",
        defaultValue: "1",
        type: "number",
        description: "Controls family-based beat to beat analysis.",
      },
      {
        name: "AnalyzeST",
        label: "AnalyzeST",
        defaultValue: "1",
        type: "number",
        description: "Controls ST analysis during beat to beat processing.",
      },
      {
        name: "ChnlMask",
        label: "ChnlMask",
        defaultValue: "3",
        type: "number",
        description: "Channel mask used by beat to beat and QT analysis.",
      },
      {
        name: "ElevationPointsInterval",
        label: "ElevationPointsInterval",
        defaultValue: "10",
        type: "number",
        description: "Interval between elevation points used for ST elevation calculations.",
      },
      {
        name: "NrOfElevationPoints",
        label: "NrOfElevationPoints",
        defaultValue: "1",
        type: "number",
        description: "Number of elevation points used by ST elevation analysis.",
      },
      {
        name: "STDisplacementMinLen",
        label: "STDisplacementMinLen",
        defaultValue: "30",
        type: "number",
        description: "Minimum length used for ST displacement detection.",
      },
      {
        name: "STElevationThreshold",
        label: "STElevationThreshold",
        defaultValue: "100",
        type: "number",
        description: "Threshold used for ST elevation detection.",
      },
      {
        name: "StartSTEPWindow",
        label: "StartSTEPWindow",
        defaultValue: "120",
        type: "number",
        description: "Start window used by ST episode processing.",
      },
      {
        name: "TimeBinDimension",
        label: "TimeBinDimension",
        defaultValue: "30",
        type: "number",
        description:
          "Time bin dimension for beat to beat batch analysis. AMPS support may recommend 120 for records that fail beat to beat processing with the default value.",
      },
    ],
  },
];

export const ANALYSIS_SETTINGS_SECTIONS = ANALYSIS_SETTINGS_FILE_SECTIONS;

export const buildDefaultAnalysisSettings = (): AnalysisSettings => {
  const settings: AnalysisSettings = {
    version: ANALYSIS_SETTINGS_VERSION,
    modules: {
      beatToBeatModule: true,
      arrhythmiaAnalysisModule: true,
      analysisModule: true,
      longAnalysisModule: false,
    },
    beatDetection: {},
    rhythmAnalysis: {},
    qtAnalysisOptions: {},
  };

  ANALYSIS_MODULE_FIELDS.forEach((field) => {
    settings.modules[field.name as keyof AnalysisSettings["modules"]] = field.defaultValue as boolean;
  });

  ANALYSIS_SETTINGS_FILE_SECTIONS.forEach((section) => {
    section.fields.forEach((field) => {
      settings[section.key][field.name] = field.defaultValue.toString();
    });
  });

  return settings;
};

export const getAnalysisSettingDefaultValue = (
  sectionKey: AnalysisSettingsSectionKey,
  fieldName: string
): AnalysisSettingValue => {
  const field = sectionKey === "modules"
    ? ANALYSIS_MODULE_FIELDS.find((item) => item.name === fieldName)
    : ANALYSIS_SETTINGS_FILE_SECTIONS
      .find((item) => item.key === sectionKey)
      ?.fields.find((item) => item.name === fieldName);

  return field?.defaultValue ?? "";
};

export const cloneAnalysisSettings = (settings: AnalysisSettings): AnalysisSettings => ({
  version: settings.version,
  modules: { ...settings.modules },
  beatDetection: { ...settings.beatDetection },
  rhythmAnalysis: { ...settings.rhythmAnalysis },
  qtAnalysisOptions: { ...settings.qtAnalysisOptions },
});

export const restoreAnalysisSetting = (
  settings: AnalysisSettings,
  sectionKey: AnalysisSettingsSectionKey,
  fieldName: string
): AnalysisSettings => {
  const restored = cloneAnalysisSettings(settings);
  const defaultValue = getAnalysisSettingDefaultValue(sectionKey, fieldName);

  if (sectionKey === "modules") {
    restored.modules[fieldName as keyof AnalysisSettings["modules"]] = defaultValue as boolean;
  } else {
    restored[sectionKey][fieldName] = defaultValue.toString();
  }

  return restored;
};
